import { toast } from 'react-toastify';
import store from '../store';
import { logout } from '../views/Login/userSlice';

const INVALID_TOKEN_TOAST_ID = 'invalid-token-session-expired';
let isLoggingOut = false;

/**
 * Checks if an API response or error indicates an invalid or expired token.
 */
export const isInvalidTokenResponse = (data, statusCode, url = '') => {
  // If the request was deliberately to the login endpoint, avoid false positives for password errors
  const isAuthEndpoint = typeof url === 'string' && (url.includes('/login') || url === 'login');

  // HTTP 401 Unauthorized is always an authentication failure
  if (statusCode === 401) {
    return !isAuthEndpoint;
  }

  if (!data) {
    return false;
  }

  // If response body is a string (e.g. gateway error message)
  if (typeof data === 'string') {
    return /invalid\s+token|token\s+is\s+invalid|token\s+expired|jwt\s+expired/i.test(data);
  }

  if (typeof data === 'object') {
    const message = String(data.message || data.error || data.msg || '').trim();

    // Check specific invalid token message patterns
    const isTokenMessage = /invalid\s+token|token\s+is\s+invalid|token\s+expired|jwt\s+expired|jwt\s+malformed|token\s+not\s+provided|invalid\s+signature/i.test(message);

    if (isTokenMessage) {
      return true;
    }

    // If status is explicitly false or 0 and message indicates unauthorized
    if ((data.status === false || data.status === 0 || data.success === false) && /unauthorized/i.test(message)) {
      return !isAuthEndpoint;
    }
  }

  return false;
};

/**
 * Clears authentication data from localStorage and Redux store.
 */
export const clearAuthSession = () => {
  try {
    localStorage.removeItem('prime_access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('Booking_as');
  } catch (err) {
    console.error('Failed to clear auth storage:', err);
  }

  try {
    if (store && typeof store.dispatch === 'function') {
      store.dispatch(logout());
    }
  } catch (err) {
    console.error('Failed to dispatch logout action:', err);
  }
};

/**
 * Redirects the user to the login page.
 */
export const redirectToLogin = () => {
  if (typeof window === 'undefined' || !window.location) {
    return;
  }

  const currentPath = window.location.pathname || '';
  const isAlreadyOnLogin = currentPath.endsWith('/login') || currentPath === '/';

  if (!isAlreadyOnLogin) {
    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
    const loginUrl = `${publicUrl}/login`.replace(/\/+/g, '/') || '/login';

    if (typeof window.location.replace === 'function') {
      window.location.replace(loginUrl);
    } else {
      window.location.href = loginUrl;
    }
  }
};

/**
 * Handles invalid token: clears session, notifies user, and redirects to login.
 */
export const handleInvalidTokenLogout = (message = 'Invalid Token!') => {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;

  clearAuthSession();

  const displayMessage = message || 'Invalid Token! Please log in again.';

  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('auth_session_message', displayMessage);
    }
  } catch (err) {
    // Ignore storage errors
  }

  try {
    toast.error(displayMessage, {
      toastId: INVALID_TOKEN_TOAST_ID,
    });
  } catch (err) {
    // Ignore toast errors if container not ready
  }

  redirectToLogin();

  // Reset locking flag after delay in case page didn't reload (e.g. already on login)
  setTimeout(() => {
    isLoggingOut = false;
  }, 2500);
};

// Export reset function primarily for testing purposes
export const _resetLoggingOutState = () => {
  isLoggingOut = false;
};
